import { useMemo } from 'react'
import { useFieldArray } from 'react-hook-form'

import { FormattedMessage, useIntl } from 'react-intl'
import { LuCirclePlus } from 'react-icons/lu'
import { getTelemetryAssetSourceId } from '@/feature/telemetry/utils'

import { SelectOption } from '@/lib/core/components/Select'
import { Label } from '@/lib/core/components/TextField/TextField.styles'
import { TooltipKeyValue, TooltipKeyValueProps } from '@/lib/core/components/TooltipKeyValue'
import { GET_ALL_LIMIT } from '@/lib/core/constants/constants'

import { Asset } from '@/lib/features/Assets/interfaces'
import messages from '@/lib/features/SmartShares/components/ShareAssetModal/ShareAssetModal.messages'
import { AssetRowFormState } from '@/lib/features/SmartShares/components/ShareAssetModal'
import { ShareRow } from '@/lib/features/SmartShares/components/ShareAssetModal/components'
import {
  AddAssetButton,
  Footer,
  Table,
  Th,
  ThAsset,
  ThRemove,
  Wrapper,
} from '@/lib/features/SmartShares/components/ShareAssetModal/components/AssetsTable'
import { Share } from '@/lib/features/SmartShares/interfaces'
import { useAllAssets } from '@/lib/features/Assets/api'

export const getOptionLabel = (asset: Partial<Asset> | null) => {
  if (!asset) {
    return ''
  }

  const { description, model, plateNumber } = asset

  return [description, plateNumber, model].filter(Boolean).join(' | ')
}

export type AssetsTableProps = {
  assetDefaultValues: AssetRowFormState
  onAddNewAsset?: () => void
  shareToExtend?: Share
  onwardShareToExtend?: Share
}

export const AssetsTable = ({
  assetDefaultValues,
  onAddNewAsset,
  shareToExtend,
  onwardShareToExtend,
}: AssetsTableProps) => {
  const { formatMessage } = useIntl()
  const assetsList = useAllAssets({ pageSize: GET_ALL_LIMIT })

  /**
   * Memoised against the results array, not the hook result: the paginated
   * request hook rebuilds its return object on every render, so keying off it
   * handed every consumer a fresh `items` array each time and made downstream
   * effects that depend on `items` fire continuously.
   */
  const items = useMemo(
    () =>
      assetsList.data.results.map(
        (asset) =>
          ({
            label: getOptionLabel(asset),
            value: asset.id,
            data: {
              asset,
              id: getTelemetryAssetSourceId(asset),
            },
          }) as SelectOption,
      ),
    [assetsList.data.results],
  )

  const durationTooltip = useMemo<TooltipKeyValueProps['content']>(
    () => [
      {
        key: `${formatMessage(messages.durationInstant)}:`,
        value: formatMessage(messages.durationInstantDescription),
      },
      {
        key: `${formatMessage(messages.durationCustom)}:`,
        value: formatMessage(messages.durationCustomDescription),
      },
    ],
    [],
  )

  const { append, fields, remove } = useFieldArray({ name: 'assets' })
  const onRowRemove = (index: number) => remove(index)

  return (
    <Wrapper>
      <Label>
        <FormattedMessage {...messages.assetSelectLabel} />
      </Label>
      <Table>
        <thead>
          <tr>
            <ThAsset>
              <FormattedMessage {...messages.assetName} />
            </ThAsset>
            <Th>Reference No</Th>
            <Th>
              <TooltipKeyValue content={durationTooltip}>
                <FormattedMessage {...messages.duration} />
              </TooltipKeyValue>
            </Th>
            <Th />
            <ThRemove>
              <FormattedMessage {...messages.remove} />
            </ThRemove>
            {/* <ThCost> */}
            {/*  <FormattedMessage {...messages.cost} /> */}
            {/* </ThCost> */}
          </tr>
        </thead>
        <tbody>
          {fields.map(({ id }, index) => (
            <ShareRow
              key={id}
              items={items}
              index={index}
              isLoading={assetsList.isLoading}
              onAddNewAsset={onAddNewAsset}
              shareToExtend={shareToExtend}
              onwardShareToExtend={onwardShareToExtend}
              onRemove={() => onRowRemove(index)}
              rows={fields.length}
              uid={id}
            />
          ))}
        </tbody>
      </Table>
      <Footer>
        <AddAssetButton
          disabled={!!shareToExtend || !!onwardShareToExtend}
          type="button"
          onClick={() => append({ ...assetDefaultValues })}
        >
          <LuCirclePlus size="24" strokeWidth={1} />
          <FormattedMessage {...messages.addAnother} tagName="span" />
        </AddAssetButton>
      </Footer>
    </Wrapper>
  )
}
